/**
 * Hook to fetch QuestionnaireResponses linked to an appointment.
 *
 * Searches by meta.tag (appointment-id tag system) and optionally by encounter.
 * For cohort Group Therapy appointments, also resolves the shared note via
 * group-shared-note extension and/or QuestionnaireResponse.subject=Group/{id}.
 * For each response, also resolves the referenced Questionnaire so the display
 * and edit components have the template definition available.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Appointment, Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import {
  getAppointmentGroupRef,
  getAppointmentSharedNoteRef,
  isCohortGroupAppointment,
} from '@/lib/groupTherapy';
import { APPOINTMENT_TAG_SYSTEM, withPreservedProviderSignature } from '../fhirNoteMapping';
import { fetchQuestionnaireForResponseRef, getQuestionnaireRefString } from '../utils/fhirHelpers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AppointmentResponseEntry {
  response: QuestionnaireResponse;
  questionnaire: Questionnaire | null;
}

interface UseAppointmentResponsesResult {
  entries: AppointmentResponseEntry[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /** Replace one response in place (e.g. after resolving a comment) so UI updates without waiting for refetch. */
  replaceResponse: (responseId: string, updated: QuestionnaireResponse) => void;
}

function qrRefKey(qr: QuestionnaireResponse): string | undefined {
  return qr.id ? `QuestionnaireResponse/${qr.id}` : undefined;
}

function dedupeResponses(responses: QuestionnaireResponse[]): QuestionnaireResponse[] {
  const seen = new Set<string>();
  const out: QuestionnaireResponse[] = [];
  for (const qr of responses) {
    const key = qr.id ?? qrRefKey(qr);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(qr);
  }
  return out;
}

async function resolveCohortSharedNotes(
  medplum: ReturnType<typeof useMedplum>,
  appointment: Appointment,
  tagged: QuestionnaireResponse[],
): Promise<QuestionnaireResponse[]> {
  const extras: QuestionnaireResponse[] = [];

  const sharedRef = getAppointmentSharedNoteRef(appointment)?.reference;
  if (sharedRef?.startsWith('QuestionnaireResponse/')) {
    const id = sharedRef.slice('QuestionnaireResponse/'.length);
    const already = tagged.find((qr) => qr.id === id);
    if (already) {
      extras.push(already);
    } else {
      try {
        extras.push(await medplum.readResource('QuestionnaireResponse', id));
      } catch {
        // Shared note extension may be stale; fall through to subject search.
      }
    }
  }

  const groupRef = getAppointmentGroupRef(appointment)?.reference;
  if (groupRef?.startsWith('Group/')) {
    try {
      const subjectQuery: Record<string, string> = {
        subject: groupRef,
        _sort: '-_lastUpdated',
        _count: '5',
      };
      if (appointment.id) {
        subjectQuery._tag = `${APPOINTMENT_TAG_SYSTEM}|${appointment.id}`;
      }
      const bySubject = await medplum.searchResources('QuestionnaireResponse', subjectQuery);
      extras.push(...bySubject);
    } catch {
      // Subject search is best-effort; tagged results still apply.
    }
  }

  // Prefer Group-subject / extension-linked shared notes ahead of patient copies.
  const sharedIds = new Set(
    extras
      .map((qr) => qr.id)
      .filter((id): id is string => Boolean(id)),
  );
  const taggedShared = tagged.filter(
    (qr) =>
      sharedIds.has(qr.id ?? '') ||
      qr.subject?.reference?.startsWith('Group/') ||
      (sharedRef != null && qrRefKey(qr) === sharedRef),
  );
  const rest = tagged.filter((qr) => !taggedShared.includes(qr) && !sharedIds.has(qr.id ?? ''));
  return dedupeResponses([...taggedShared, ...extras, ...rest]);
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAppointmentResponses(
  appointmentId: string | undefined,
  appointment?: Appointment | null,
): UseAppointmentResponsesResult {
  const medplum = useMedplum();
  const [entries, setEntries] = useState<AppointmentResponseEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedOnceRef = useRef(false);
  const isCohort = Boolean(appointment && isCohortGroupAppointment(appointment));
  const cohortGroupRef = appointment ? getAppointmentGroupRef(appointment)?.reference : undefined;
  const cohortSharedNoteRef = appointment
    ? getAppointmentSharedNoteRef(appointment)?.reference
    : undefined;
  const appointmentRef = useRef(appointment);
  appointmentRef.current = appointment;

  useEffect(() => {
    hasLoadedOnceRef.current = false;
  }, [appointmentId]);

  const doFetch = useCallback(async (): Promise<void> => {
    if (!appointmentId) {
      setEntries([]);
      setLoading(false);
      hasLoadedOnceRef.current = false;
      return;
    }

    const isBackgroundRefresh = hasLoadedOnceRef.current;
    if (!isBackgroundRefresh) {
      setLoading(true);
    }
    setError(null);

    try {
      medplum.invalidateSearches('QuestionnaireResponse');
      let responses: QuestionnaireResponse[] = await medplum.searchResources('QuestionnaireResponse', {
        _tag: `${APPOINTMENT_TAG_SYSTEM}|${appointmentId}`,
        _sort: '-_lastUpdated',
        _count: '20',
      });

      const appt = appointmentRef.current;
      if (isCohort && appt) {
        responses = await resolveCohortSharedNotes(medplum, appt, responses);
      }

      // Resolve Questionnaires for each response (batch/deduplicate)
      const questionnaireRefs = new Set<string>();
      for (const qr of responses) {
        const refStr = getQuestionnaireRefString(qr.questionnaire);
        if (refStr) questionnaireRefs.add(refStr);
      }

      const questionnaireMap = new Map<string, Questionnaire>();
      await Promise.all(
        [...questionnaireRefs].map(async (ref) => {
          const q = await fetchQuestionnaireForResponseRef(medplum, ref);
          if (q) questionnaireMap.set(ref, q);
        }),
      );

      const result: AppointmentResponseEntry[] = responses.map((qr) => {
        const refStr = getQuestionnaireRefString(qr.questionnaire);
        return {
          response: qr,
          questionnaire: refStr ? questionnaireMap.get(refStr) ?? null : null,
        };
      });

      setEntries((prev) => {
        const previousById = new Map(
          prev
            .map((entry) => [entry.response.id, entry.response] as const)
            .filter((pair): pair is readonly [string, QuestionnaireResponse] => Boolean(pair[0])),
        );
        return result.map((entry) => ({
          ...entry,
          response: entry.response.id
            ? withPreservedProviderSignature(previousById.get(entry.response.id), entry.response)
            : entry.response,
        }));
      });
      hasLoadedOnceRef.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch responses');
    } finally {
      setLoading(false);
    }
  }, [medplum, appointmentId, isCohort, cohortGroupRef, cohortSharedNoteRef]);

  const replaceResponse = useCallback((responseId: string, updated: QuestionnaireResponse) => {
    setEntries((prev) => {
      const idx = prev.findIndex((e) => e.response.id === responseId);
      if (idx >= 0) {
        return prev.map((e, i) =>
          i === idx
            ? { ...e, response: withPreservedProviderSignature(e.response, updated) }
            : e,
        );
      }
      // First save (create) — entry not in list yet; append so UI can merge before refetch completes.
      return [...prev, { response: updated, questionnaire: null }];
    });
  }, []);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  return { entries, loading, error, refetch: doFetch, replaceResponse };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
